import { MouseEvent, useState } from 'react'

import { lazyRenderWidget } from 'core-components/helpers/lazyLoad'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { Flex } from 'core-components/library/flex'

import { IProperties, TPropertiesValue } from '../../models/IProperties'

import { StaticDropdown } from './components/StaticDropdown'
import { TooltipButtonWrapper } from './components/TooltipButtonWrapper'
import { DefaultRender } from './DefaulRender'
import { IPropertiesEnumValuesWithHierarchy } from './models/IPropertiesEnumValuesWithHierarchy'

import styles from './ArrayObjectRender.module.scss'

interface IArrayRender {
  property: IProperties
  updateData: () => void
}
export const ArrayObjectRender = ({ property, updateData }: IArrayRender) => {
  const propertyValue = property.value as object[]
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: MouseEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const draggedItem = propertyValue[draggedIndex]
    const newPropertyValue = [...propertyValue]
    newPropertyValue.splice(draggedIndex, 1)
    newPropertyValue.splice(index, 0, draggedItem)
    property.value = newPropertyValue
    setDraggedIndex(index)
    updateData()
  }

  const handleDrop = () => {
    setDraggedIndex(null)
  }

  return (
    <Flex flexDirection={'column'} className={'content-full-width'}>
      <Flex flexDirection={'column'} gap={'md'}>
        {propertyValue.map((el, index) => {
          return (
            <div
              key={`property-value_${property.name}_${index}`}
              className={draggedIndex === index ? styles.itemDragging : ''}
            >
              <Flex>
                <Flex flexDirection={'column'} className={'content-full-width'} gap={'xs'}>
                  {property.objectDescription?.map((p) => {
                    const commonProps = {
                      className: 'content-full-width',
                      value: typeof el[p.name] !== 'undefined' ? el[p.name] : p.value,
                      label: p.name,
                      allowSelectAll: p.allowSelectAll,
                      onChange: (newValue: TPropertiesValue) => {
                        propertyValue[index][p.name] = newValue
                        updateData()
                      },
                    }

                    const tooltipButtonProps = { modalPath: p.tooltipButtonModalContentPath }

                    if (p.enumValues?.length) {
                      const itemsData: IPropertiesEnumValuesWithHierarchy[] =
                        [...p.enumValues] || []

                      let hasHierarchy = false

                      itemsData.forEach((item) => {
                        if (!item.parent) {
                          item.parent = 'null'
                        }

                        const parent = itemsData.find((el) => el.key === item.parent)
                        if (parent) {
                          hasHierarchy = true
                          parent.hasChildren = true
                        }
                      })

                      return (
                        <TooltipButtonWrapper key={p.name} {...tooltipButtonProps}>
                          <StaticDropdown
                            {...commonProps}
                            defaultValue={propertyValue[index]['renderTypeDefaultValue']}
                            onDefaultValueChange={(newDefault) => {
                              propertyValue[index]['renderTypeDefaultValue'] = newDefault
                              updateData()
                            }}
                            hasHierarchy={hasHierarchy}
                            itemsData={itemsData}
                          />
                        </TooltipButtonWrapper>
                      )
                    }

                    if (p.type === 'boolean') {
                      return <Switch {...commonProps} checked={commonProps.value} />
                    }

                    if (p.type === 'custom' && p.customEditor) {
                      return (
                        <TooltipButtonWrapper key={p.name} {...tooltipButtonProps}>
                          {lazyRenderWidget(p.customEditor, {
                            properties: [property],
                            label: commonProps.label,
                            value: commonProps.value,
                            valueChanged: commonProps.onChange,
                            allowSelectAll: commonProps.allowSelectAll,
                          })}
                        </TooltipButtonWrapper>
                      )
                    }

                    return (
                      <TooltipButtonWrapper key={p.name} {...tooltipButtonProps}>
                        <DefaultRender property={p} withInnerValidator={true} {...commonProps} />
                      </TooltipButtonWrapper>
                    )
                  })}
                </Flex>

                <Flex flexShrink={0}>
                  <div
                    onMouseDown={() => handleDragStart(index)}
                    onMouseOver={(e) => handleDragOver(e, index)}
                    onMouseUp={handleDrop}
                    title={`Reorder by D'n'D`}
                  >
                    <Button
                      className={styles.dndButton}
                      color={'grey'}
                      variant={'outlined'}
                      iconButton={'kbase kbase-spoc-drag'}
                    />
                  </div>

                  <div>
                    <Button
                      iconButton={'kbase kbase-delete-alt'}
                      variant={'outlined'}
                      color={'danger'}
                      onClick={() => {
                        propertyValue.splice(index, 1)
                        updateData()
                      }}
                    />
                  </div>
                </Flex>
              </Flex>
            </div>
          )
        })}
      </Flex>

      <div>
        <Button
          icon={'kbase kbase-add'}
          onClick={() => {
            const newArrayItem = {}
            property.objectDescription?.map((item) => {
              newArrayItem[item.name] = ''
            })
            propertyValue.push(newArrayItem)
            updateData()
          }}
        >
          Add item
        </Button>
      </div>
    </Flex>
  )
}
